import { useEffect, useRef, useState, useMemo, useCallback, memo } from "react"
import DataGrid, { MTableToolbar, Options, Query } from "@material-table/core"
import { UserProfilePayload } from "../models/UserProfilePayload"
import { profile } from "../models/profile"
import { getActiveUsersNo, getApiUserList } from "../services/user"
import moment from "moment"
import IconButton from "@mui/material/IconButton"
import Icon from "@mui/material/Icon"
import { useNavigate, useSearchParams } from "react-router-dom"
import icon from "../export-excel.png"
import { Typography } from "@mui/material"
import { exportToExcel } from "../utils/exportExcel"

/** Format gender 0|1|2 to display string */
const formatGender = (gender?: profile["gender"]): string => {
  if (!gender) return "Not Specified"
  if (gender === 1) return "Male"
  if (gender === 2) return "Female"
  return "Not Specified"
}

/** Format boolean active to title case string */
const formatAvailability = (active?: boolean): string =>
  active ? active.toString().charAt(0).toUpperCase() + active.toString().slice(1) : ""

/** Format movement age with diff from birth date */
const formatMovementAge = (movementAge?: number, birthDate?: string): string => {
  const base = movementAge ?? "Not specified"
  if (!movementAge || !birthDate) return String(base)
  const age = moment().diff(birthDate, "years")
  const diff = movementAge - age
  return `${movementAge} ( ${diff >= 0 ? "+" : ""}${diff} )`
}

/** Format date or return null */
const formatDate = (date?: string): string | null =>
  date ? moment(date).format("L") : null

/** Convert user to export row shape */
const userToExportRow = (user: UserProfilePayload) => ({
  firstName: user.profile?.firstName,
  lastName: user.profile?.lastName,
  email: user.email,
  dob: formatDate(user.profile?.birthDate) ?? null,
  movementAge: formatMovementAge(user.movementAge, user.profile?.birthDate),
  gender: formatGender(user.profile?.gender),
  accountType: user.authType,
  availability: formatAvailability(user.active),
  licenceExpiration: formatDate(user.licenceExpiration) ?? "Not specified",
  token: user.subscription?.token ?? '-',
  productId: user.subscription?.product_id ?? '-',
  status: user.licenceExpiration ? 'Subscribed' : 'Free'
})

const EXPORT_COLUMNS: { label: string; value: string }[] = [
  { label: "First Name", value: "firstName" },
  { label: "Last Name", value: "lastName" },
  { label: "Email", value: "email" },
  { label: "DOB", value: "dob" },
  { label: "Movement age", value: "movementAge" },
  { label: "Gender", value: "gender" },
  { label: "Account Type", value: "accountType" },
  { label: "Availability", value: "availability" },
  { label: "Licence expiration", value: "licenceExpiration" },
  { label: "Subscription Token", value: "token" },
  { label: "Subscription Product ", value: "productId" },
  { label: "Subscription Status ", value: "status" },
]

const download = async () => {
  const { items } = await getApiUserList()
  await exportToExcel("Users", [
    { name: "Users", data: items.map(userToExportRow), columns: EXPORT_COLUMNS },
  ])
}

const ActionExport = memo((props: { action: { icon: string; onClick: () => void } }) => {
  if (props.action.icon === "export-csv") {
    return (
      <IconButton onClick={props.action.onClick} size="small">
        <img alt="export-excel" src={String(icon)} style={{ width: 24, height: 24 }} />
      </IconButton>
    )
  }
  return null
})

const ToolbarWithCount = memo((props: Record<string, unknown> & { count: number }) => (
  <div>
    <MTableToolbar {...props} />
    <Typography variant="subtitle2" style={{ marginLeft: 24, marginTop: -16, color: "red" }}>
      {`# of Active Users: ${props.count}`}
    </Typography>
  </div>
))

export const UserList = () => {
  const history = useNavigate()
  const ref = useRef<any>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeUsersNo, setActiveUsersNo] = useState<number>(0)
  const lastSearchRef = useRef<string>("")
  const debounceTimerRef = useRef<number | undefined>(undefined)
  const debounceMs = 300

  const urlPage = Math.max(0, Number(searchParams.get("page") ?? 0) || 0)
  const urlPageSize = Math.max(1, Number(searchParams.get("pageSize") ?? 10) || 10)
  const urlSearch = searchParams.get("search") ?? ""
  const urlSortBy = (searchParams.get("sortBy") ?? "").trim() || undefined
  const urlSortOrder = (
    searchParams.get("sortOrder") === "asc"
      ? "asc"
      : searchParams.get("sortOrder") === "desc"
        ? "desc"
        : undefined
  ) as "asc" | "desc" | undefined

  const setUrlParams = useCallback((next: {
    page?: number
    pageSize?: number
    search?: string
    sortBy?: string
    sortOrder?: "asc" | "desc"
  }) => {
    const current = searchParams.toString()
    const sp = new URLSearchParams(searchParams)
    if (typeof next.page === "number") sp.set("page", String(Math.max(0, next.page)))
    if (typeof next.pageSize === "number") sp.set("pageSize", String(Math.max(1, next.pageSize)))
    if (typeof next.search === "string") {
      const v = next.search.trim()
      if (v) sp.set("search", v)
      else sp.delete("search")
    }
    if (typeof next.sortBy === "string") {
      const v = next.sortBy.trim()
      if (v) sp.set("sortBy", v)
      else sp.delete("sortBy")
    }
    if (next.sortOrder === "asc" || next.sortOrder === "desc") sp.set("sortOrder", next.sortOrder)
    else if (next.sortOrder === undefined) sp.delete("sortOrder")
    const nextStr = sp.toString()
    if (nextStr !== current) setSearchParams(sp, { replace: true })
  }, [searchParams, setSearchParams])

  const columns = useMemo(
    () => [
      {
        title: "First Name",
        field: "firstName",
        defaultSort: urlSortBy === "firstName" ? urlSortOrder : undefined,
        render: (e: UserProfilePayload) => <>{e.profile?.firstName}</>,
        cellStyle: { width: "15%" },
      },
      {
        title: "Last Name",
        field: "lastName",
        defaultSort: urlSortBy === "lastName" ? urlSortOrder : undefined,
        render: (e: UserProfilePayload) => <>{e.profile?.lastName}</>,
        cellStyle: { width: "15%" },
      },
      { title: "Email", field: "email", defaultSort: urlSortBy === "email" ? urlSortOrder : undefined, cellStyle: { width: "20%" } },
      {
        title: "DOB",
        field: "birthDate",
        defaultSort: urlSortBy === "birthDate" ? urlSortOrder : undefined,
        render: (e: UserProfilePayload) => <>{formatDate(e.profile?.birthDate)}</>,
        cellStyle: { width: "8%" },
      },
      {
        title: "Movement Age",
        field: "movementAge",
        defaultSort: urlSortBy === "movementAge" ? urlSortOrder : undefined,
        render: (e: UserProfilePayload) => (
          <>{formatMovementAge(e.movementAge, e.profile?.birthDate)}</>
        ),
        cellStyle: { width: "8%" },
      },
      {
        title: "Gender",
        field: "gender",
        render: (e: UserProfilePayload) => <>{formatGender(e.profile?.gender)}</>,
        cellStyle: { width: "5%" },
      },
      {
        title: "Account Type",
        field: "authType",
        defaultSort: urlSortBy === "authType" ? urlSortOrder : undefined,
        cellStyle: { width: "8%" },
      },
      {
        title: "Availability",
        field: "active",
        defaultSort: urlSortBy === "active" ? urlSortOrder : undefined,
        render: (e: UserProfilePayload) => <>{formatAvailability(e.active)}</>,
        cellStyle: { width: "5%" },
      },
      {
        title: "Licence Expiration",
        field: "licenceExpiration",
        defaultSort: urlSortBy === "licenceExpiration" ? urlSortOrder : undefined,
        render: (e: UserProfilePayload) => (
          <>{formatDate(e.licenceExpiration) ?? "Not specified"}</>
        ),
        cellStyle: { width: "10%" },
      },
      {
        title: "Tools",
        sorting: false,
        render: (e: UserProfilePayload) => (
          <IconButton onClick={() => history(`/app/users/edit/${e.id}`)} style={{ float: "right" }}>
            <Icon>edit</Icon>
          </IconButton>
        ),
      },
    ],
    [history, urlSortBy, urlSortOrder]
  )

  const dataLoader = useCallback(async (q: Query<UserProfilePayload>) => {
    const search = (q.search ?? "").trim()
    const searchChanged = search !== lastSearchRef.current

    if (searchChanged) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      await new Promise<void>((r) => {
        debounceTimerRef.current = window.setTimeout(r, debounceMs) as unknown as number
      })
    }

    const pageIndex = searchChanged ? 0 : (q.page ?? 0)
    const pageSize = q.pageSize ?? 10
    const skip = pageIndex * pageSize

    const rawSortBy = q.orderBy?.field
    const sortBy = Array.isArray(rawSortBy) ? rawSortBy[0] : rawSortBy
    const sortOrder = q.orderDirection

    setUrlParams({
      page: pageIndex,
      pageSize,
      search,
      sortBy: typeof sortBy === "string" ? sortBy : "",
      sortOrder,
    })
    const res = await getApiUserList({ skip, limit: pageSize, search, sortBy, sortOrder })
    lastSearchRef.current = search

    const totalCount =
      typeof res.total === "number"
        ? res.total
        : pageIndex * pageSize + res.items.length
    const allowedMaxPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1)
    const safePage = Math.min(pageIndex, allowedMaxPage)

    return { data: res.items, page: safePage, totalCount }
  }, [setUrlParams])

  const actions = useMemo(
    () => [
      {
        tooltip: "Export CSV",
        isFreeAction: true,
        disabled: false,
        icon: "export-csv" as const,
        onClick: download,
      },
    ],
    []
  )

  const components = useMemo(
    () => ({
      Toolbar: (props: Record<string, unknown>) => (
        <ToolbarWithCount {...props} count={activeUsersNo} />
      ),
      Action: ActionExport,
    }),
    [activeUsersNo]
  )

  const options = useMemo<Options<UserProfilePayload>>(
    () => ({
      actionsColumnIndex: -1,
      search: true,
      maxColumnSort: 1,
      pageSize: urlPageSize,
      initialPage: urlPage,
      searchText: urlSearch,
      columnsButton: true,
    }),
    [urlPage, urlPageSize, urlSearch]
  )

  useEffect(() => {
    const getActiveUsers = async () => {
      const data = await getActiveUsersNo()
      setActiveUsersNo(data.count)
    }
    getActiveUsers()
  }, [])

  return (
    <div>
      <DataGrid<UserProfilePayload>
        title="Users"
        options={options}
        tableRef={ref}
        columns={columns}
        data={dataLoader}
        actions={actions}
        components={components}
      />
    </div>
  )
}
